import is from '@sindresorhus/is';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useContext } from 'react';

import { getSortParams } from './common';
import { ApplicationContext } from '../../../../../shared/source/context/context';
import {
    globalSearchRelatedSkillsResponseSchema,
    globalSearchSkillResponseSchema,
    globalSearchTermSchema,
} from '../../../data-contract/global-search';

import type { Pathname } from '../../../../../shared/source/http-fetcher/http-fetcher';
import type {
    GlobalSearchBySkillIdsParams,
    GlobalSearchParams,
    GlobalSearchRelatedSkillsResponse,
    GlobalSearchSkillResponse,
} from '../../../data-contract/global-search';
import type { UseQueryResult } from '@tanstack/react-query';

export const useGlobalSearchSkillsResultQuery = ({
    pagination,
    searchTerm,
    sortedFields,
}: GlobalSearchParams): UseQueryResult<GlobalSearchSkillResponse> => {
    const { applicationState, validatingCobrainerApiFetcher } = useContext(ApplicationContext);

    const params = new URLSearchParams({
        page: pagination.page.toString(),
        searchTerm,
        size: pagination.size.toString(),
    });

    if (is.array(sortedFields) && is.nonEmptyArray(sortedFields)) {
        params.append('sort', getSortParams(sortedFields));
    }

    const requestOptions = {
        searchParams: params.toString(),
    };

    const pathname: Pathname = '/global/search/skills';

    return useQuery({
        enabled: globalSearchTermSchema.safeParse(searchTerm).success,
        placeholderData: keepPreviousData,
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(globalSearchSkillResponseSchema, {
                pathname,
                requestOptions,
            });
            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [pathname, 'GET', applicationState.language.code, requestOptions],
    });
};

export const useGlobalSearchRelatedSkillsQuery = ({
    pagination,
    skillIds,
    sortedFields,
}: GlobalSearchBySkillIdsParams): UseQueryResult<GlobalSearchRelatedSkillsResponse> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);

    const pathname: Pathname = '/global/skill-id/skills';

    const searchParams: Record<string, number | string> = {
        page: pagination.page,
        size: pagination.size,
        skillIds: skillIds.join(','),
    };

    if (is.array(sortedFields) && is.nonEmptyArray(sortedFields)) {
        searchParams['sort'] = getSortParams(sortedFields);
    }

    return useQuery({
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(globalSearchRelatedSkillsResponseSchema, {
                pathname,
                requestOptions: {
                    searchParams,
                },
            });
            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [pathname, 'GET', searchParams],
    });
};
